import React, { ComponentProps } from "react";
import { rgba } from "polished";
import styled from "styled-components";

import { MerchProduct } from "@services/merch/types/api/merch-product";

import { Info } from "../link-components/text-info";
import { CoverImage } from "../cover-image";
import { WrapperInfo } from "../link-components/wrapper-info";
import ArrowRight from "../../icons/arrow-right";
import { TracPageFeatureItemType } from "@services/tracpage/types/trac-page-feature-item";
import { VisisbilityButtons } from "../edit-controls/visibility-buttons";
import { useEditMode } from "../../contexts/edit";
import { SectionIdentifier } from "@services/tracpage/types/trac-page-section-id";
import { ItemsOrderButtons } from "../edit-controls/items-order-buttons";

export const ProductItemAlt = ({
  product,
  handleClick,
  hidden = false,
  noReorder = false,
  orderButtonsDisabledState,
  isMainPage = false,
}: {
  product: MerchProduct;
  handleClick?: () => void;
  hidden?: boolean;
  noReorder?: boolean;
  orderButtonsDisabledState?: ComponentProps<
    typeof ItemsOrderButtons
  >["disabledState"];
  isMainPage?: boolean;
}) => {
  const { editMode } = useEditMode();

  const haveWhite = product.colors.find(
    (el) => el.name === "White" || el.name === "white"
  );
  const defaultColorImage =
    product.colors[0]?.image || product.colors[0]?.images?.[0].src;

  if (haveWhite && !product.image) {
    product.image = haveWhite.image || product.image;
  }

  if (defaultColorImage && !product.image) {
    product.image = defaultColorImage;
  }

  return (
    <ProductContainer hidden={hidden} editMode={editMode}>
      <div className="product-info">
        <div className="cover-image">
          <CoverImage
            src={product.image}
            alt={product.title}
            variant="squareSmall"
          />
        </div>
        <WrapperInfo>
          <Info>
            <h3>{product.title}</h3>
            <p>{product.price} USD</p>
          </Info>
        </WrapperInfo>
        <div>
          <button className="icon-wrapper" onClick={handleClick}>
            <ArrowRight className="arrow-icon" />
          </button>
        </div>
      </div>

      <div className="edit-controls">
        <VisisbilityButtons
          disabledState={{ feature: hidden }}
          itemId={product.id ?? ""}
          sectionId={SectionIdentifier.MERCH}
          itemType={TracPageFeatureItemType.MerchProduct}
        />
        {!noReorder && (
          <ItemsOrderButtons
            itemId={product.id}
            sectionId={SectionIdentifier.MERCH}
            disabledState={orderButtonsDisabledState}
            forMainPageOrder={isMainPage}
          />
        )}
      </div>
    </ProductContainer>
  );
};

const ProductContainer = styled.div<{ editMode: boolean; hidden: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  width: 100%;

  border: ${({ editMode, theme }) =>
    editMode ? `1px solid ${rgba(theme.colors.primary, 0.1)}` : "inherit"};
  padding: ${({ editMode }) => (editMode ? "24px" : "0")};

  .edit-controls {
    opacity: 1;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
  }

  .product-info {
    opacity: ${({ hidden }) => (hidden ? 0.5 : 1)};

    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 16px;
    width: 100%;

    .cover-image {
      position: relative;
      width: ${({ theme }) => theme.sizes.coverSizeSmall};
      height: ${({ theme }) => theme.sizes.coverSizeSmall};
    }
  }

  .icon-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    aspect-ratio: 1;
    border-radius: 50%;
    background-color: ${({ theme }) => rgba(theme.colors.primary, 0.1)};
    cursor: pointer;
    padding: 0;
    overflow: hidden;
    border: 1px solid transparent;
    transition: background-color
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`},
      border
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`};

    > svg {
      fill: ${({ theme }) => theme.colors.primary};
      max-height: 12px;
      max-width: 16px;
      width: 100%;
      path {
        fill: ${({ theme }) => theme.colors.primary};
      }
    }

    .arrow-icon {
      fill: ${({ theme }) => theme.colors.primary};
      path {
        fill: ${({ theme }) => theme.colors.primary};
      }
    }

    span {
      color: ${({ theme }) => theme.colors.primary};
      font-size: 12px;
      font-weight: 700;
    }
    &:focus,
    &:focus-within,
    &:focus-visible,
    &:hover,
    &.active {
      background-color: ${({ theme }) => theme.colors.background};
      border-color: ${({ theme }) => theme.colors.primary};
    }

    &:focus,
    &:focus-within,
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 4px 2px ${({ theme }) => rgba(theme.colors.primary, 0.25)};
    }

    .icon {
      width: 8px;
      height: 8px;
    }
  }
`;
